// Every edit the property panel makes to a chart, as pure functions.
//
// **Why these are not inline in the panel.** The IR expresses one editing
// concept in several places depending on the viz type — a scorecard's measure
// lives in `query.metric` while a bar chart's live in `query.metrics`; a
// timeseries' dimension is `query.x_axis` while a pie's is `query.groupby[0]`;
// a number format is `y_axis_format` on two displays and `number_format` on a
// third. A panel that reads and writes those in place ends up with a `switch`
// per control and a hybrid state the first time one is missed (both `metric`
// and `metrics` populated, which is exactly the shape a compiler then has to
// guess at). One reader and one writer per concept, tested, is the fix.
//
// The `display` casts below all sit inside a `viz_type` branch, per this
// folder's rule: `ChartSpec.display` carries no discriminant on the wire, so
// the viz type is the only thing that makes a cast safe.
import type {
  AnnotationSpec,
  BiSource,
  BiSourceColumn,
  ChartSpec,
  PieDisplay,
  SimpleAggregate,
  SimpleMetric,
  Schemas,
  TimeSeriesDisplay,
} from "@bi/shared/types";

/** One `ORDER BY` entry. Aliased off `Schemas` for the same reason
 *  `canvas-layout.ts` aliases `LayoutRow`: the shared package names the query
 *  but not this leaf of it. */
type OrderBy = Schemas["OrderBy"];

import { aggregatesForColumn } from "./built-chart";
import { GRAIN_ORDER, type TimeGrain } from "./bucket-format";
import { metricLabelFor } from "./studio-model";

/** The grains a canvas may pick, in order. The compiler narrows a grain a
 *  chosen window cannot express, so this list is the ASK rather than the
 *  promise — `ChartCard` already tells the reader when the two differ. */
export const TIME_GRAINS: readonly TimeGrain[] = GRAIN_ORDER;

export const TIME_GRAIN_LABELS: Record<TimeGrain, string> = {
  PT1M: "Minute",
  PT1H: "Hour",
  P1D: "Day",
  P1W: "Week",
  P1M: "Month",
  P3M: "Quarter",
  P1Y: "Year",
};

/** The viz types that hold their measure in `query.metric` (singular). */
function isSingleMetricViz(vizType: ChartSpec["viz_type"]): boolean {
  return (
    vizType === "big_number" ||
    vizType === "big_number_total" ||
    vizType === "pie" ||
    vizType === "gauge_chart"
  );
}

function isTimeSeriesViz(vizType: ChartSpec["viz_type"]): boolean {
  return vizType === "echarts_timeseries_bar" || vizType === "echarts_timeseries_line";
}

function withQuery(chart: ChartSpec, patch: Partial<ChartSpec["query"]>): ChartSpec {
  return { ...chart, query: { ...chart.query, ...patch } };
}

/** This chart's measures, wherever the IR keeps them for its viz type. */
export function getMeasures(chart: ChartSpec): SimpleMetric[] {
  const all = isSingleMetricViz(chart.viz_type)
    ? chart.query.metric
      ? [chart.query.metric]
      : []
    : (chart.query.metrics ?? []);
  return all.filter((m): m is SimpleMetric => m.kind === "simple");
}

/**
 * Write the measures back, into the ONE field this viz type reads and clearing
 * the other.
 *
 * The clearing half is the load-bearing half. A chart with both `metric` and
 * `metrics` populated is the hybrid shape the compiler has no rule for, and it
 * is trivially produced by switching a bar chart to a scorecard and back.
 * A single-metric viz keeps only the FIRST measure, which is a real loss and
 * is why the panel says so rather than silently dropping the rest.
 */
export function setMeasures(chart: ChartSpec, measures: readonly SimpleMetric[]): ChartSpec {
  if (isSingleMetricViz(chart.viz_type)) {
    return withQuery(chart, { metric: measures[0] ?? null, metrics: [] });
  }
  return withQuery(chart, { metrics: [...measures], metric: null });
}

/** How many measures this viz type can draw. A scorecard shows one number. */
export function maxMeasures(chart: ChartSpec): number {
  return isSingleMetricViz(chart.viz_type) ? 1 : 10;
}

export function newMeasure(column: BiSourceColumn): SimpleMetric {
  const aggregate = (aggregatesForColumn(column)[0] ?? "count") as SimpleAggregate;
  const preferred: SimpleAggregate = column.role === "measure" ? "sum" : "count_distinct";
  const chosen = aggregatesForColumn(column).includes(preferred) ? preferred : aggregate;
  return {
    kind: "simple",
    aggregate: chosen,
    column: column.name,
    label: metricLabelFor(column.name, chosen),
  };
}

/** Change one measure's aggregate, RELABELLING it when the label was the one
 *  this module generated. A label somebody typed is theirs and survives; a
 *  generated one that still says "Total projects" after the aggregate moved to
 *  average is a mislabelled number, which is worse than no label. */
export function setMeasureAggregate(
  measure: SimpleMetric,
  aggregate: SimpleAggregate,
): SimpleMetric {
  const generated = measure.label === metricLabelFor(measure.column ?? "", measure.aggregate);
  return {
    ...measure,
    aggregate,
    label: generated ? metricLabelFor(measure.column ?? "", aggregate) : measure.label,
  };
}

/** The chart's main dimension: the x axis for a time series, the grouping
 *  column for anything else. */
export function getDimension(chart: ChartSpec): string | null {
  if (isTimeSeriesViz(chart.viz_type)) return chart.query.x_axis ?? null;
  return chart.query.groupby?.[0] ?? null;
}

export function setDimension(chart: ChartSpec, column: string | null): ChartSpec {
  if (isTimeSeriesViz(chart.viz_type)) {
    // The x axis of a time series is also what the runtime date range binds
    // against, so the two move together — leaving `granularity_sqla` pointing
    // at the old column filters on one column and plots another.
    return withQuery(chart, {
      x_axis: column,
      time: { ...chart.query.time, granularity_sqla: column, time_range: chart.query.time?.time_range ?? "No filter" },
    });
  }
  const rest = (chart.query.groupby ?? []).slice(1);
  return withQuery(chart, { groupby: column ? [column, ...rest] : rest });
}

/** The BREAKDOWN: a second grouping column that turns a series into one series
 *  per value. On a time series this is `groupby` (the x axis is separate); on a
 *  non-time chart it is the second `groupby` entry. */
export function getBreakdown(chart: ChartSpec): string | null {
  if (isTimeSeriesViz(chart.viz_type)) return chart.query.groupby?.[0] ?? null;
  return chart.query.groupby?.[1] ?? null;
}

export function setBreakdown(chart: ChartSpec, column: string | null): ChartSpec {
  if (isTimeSeriesViz(chart.viz_type)) {
    return withQuery(chart, { groupby: column ? [column] : [] });
  }
  const first = chart.query.groupby?.[0];
  const groupby = [first, column].filter((c): c is string => Boolean(c));
  return withQuery(chart, { groupby });
}

export function getSort(chart: ChartSpec): OrderBy | null {
  return chart.query.order_by?.[0] ?? null;
}

export function setSort(chart: ChartSpec, column: string | null, ascending: boolean): ChartSpec {
  return withQuery(chart, { order_by: column ? [{ column, ascending }] : [] });
}

/** Every name a sort may legally take: this chart's own metric labels (which
 *  the compiler resolves to the metric's alias) plus the source's columns. */
export function sortableNames(chart: ChartSpec, source: BiSource | null): string[] {
  const labels = getMeasures(chart).map((m) => m.label);
  const columns = (source?.columns ?? []).map((c) => c.name);
  return [...new Set([...labels, ...columns])];
}

export function getRowLimit(chart: ChartSpec): number | null {
  return chart.query.row_limit ?? null;
}

export function setRowLimit(chart: ChartSpec, limit: number | null): ChartSpec {
  return withQuery(chart, { row_limit: limit });
}

export function getTimeGrain(chart: ChartSpec): string | null {
  return chart.query.time?.time_grain_sqla ?? null;
}

export function setTimeGrain(chart: ChartSpec, grain: string | null): ChartSpec {
  return withQuery(chart, {
    time: {
      ...chart.query.time,
      time_grain_sqla: grain,
      time_range: chart.query.time?.time_range ?? "No filter",
    },
  });
}

/** The named colour scheme, for the two displays that carry one. `null` for a
 *  viz type with no scheme of its own (a table, a scorecard) rather than a
 *  made-up default, so the panel can hide the control instead of offering a
 *  choice that writes a field nothing reads. */
export function getColorScheme(chart: ChartSpec): string | null {
  switch (chart.viz_type) {
    case "echarts_timeseries_bar":
    case "echarts_timeseries_line":
      return (chart.display as TimeSeriesDisplay).color_scheme;
    case "pie":
      return (chart.display as PieDisplay).color_scheme;
    default:
      return null;
  }
}

export function setColorScheme(chart: ChartSpec, scheme: string): ChartSpec {
  switch (chart.viz_type) {
    case "echarts_timeseries_bar":
    case "echarts_timeseries_line":
    case "pie":
      return { ...chart, display: { ...chart.display, color_scheme: scheme } };
    default:
      return chart;
  }
}

/** The d3-style number format string this chart applies to its values, under
 *  whichever field its display calls it. */
export function getNumberFormat(chart: ChartSpec): string | null {
  switch (chart.viz_type) {
    case "echarts_timeseries_bar":
    case "echarts_timeseries_line":
      return (chart.display as TimeSeriesDisplay).y_axis_format ?? null;
    case "pie":
      return (chart.display as PieDisplay).number_format ?? null;
    case "big_number":
    case "big_number_total":
      return (chart.display as { y_axis_format?: string | null }).y_axis_format ?? null;
    default:
      return null;
  }
}

export function setNumberFormat(chart: ChartSpec, format: string | null): ChartSpec {
  const value = format && format.trim() ? format.trim() : null;
  switch (chart.viz_type) {
    case "echarts_timeseries_bar":
    case "echarts_timeseries_line":
    case "big_number":
    case "big_number_total":
      return { ...chart, display: { ...chart.display, y_axis_format: value } };
    case "pie":
      return { ...chart, display: { ...chart.display, number_format: value } };
    default:
      return chart;
  }
}

/** A scorecard's trend line. `big_number_total` is one number; `big_number` is
 *  the same number WITH a series behind it, so the toggle changes the viz type
 *  rather than a display flag — and the series needs a time axis, which is why
 *  turning it on also binds one. */
export function setTrendLine(chart: ChartSpec, on: boolean, source: BiSource | null): ChartSpec {
  if (chart.viz_type !== "big_number" && chart.viz_type !== "big_number_total") return chart;
  if (!on) {
    return {
      ...chart,
      viz_type: "big_number_total",
      display: { ...chart.display, show_trend_line: false },
    };
  }
  const time = (source?.columns ?? []).find((c) => c.role === "time")?.name ?? null;
  const withAxis = time
    ? withQuery(chart, {
        time: {
          ...chart.query.time,
          granularity_sqla: time,
          time_grain_sqla: chart.query.time?.time_grain_sqla ?? "P1D",
          time_range: chart.query.time?.time_range ?? "No filter",
        },
      })
    : chart;
  return {
    ...withAxis,
    viz_type: "big_number",
    display: { ...withAxis.display, show_trend_line: true },
  };
}

export function getAnnotations(chart: ChartSpec): AnnotationSpec | null {
  if (!isTimeSeriesViz(chart.viz_type)) return null;
  return (chart.display as TimeSeriesDisplay).annotations ?? null;
}

/** Turn a mark set on or off. Only a time series carries one: marks are a
 *  position on a time axis, and a chart with no time axis has nowhere to put
 *  them. */
export function setAnnotations(chart: ChartSpec, annotations: AnnotationSpec | null): ChartSpec {
  if (!isTimeSeriesViz(chart.viz_type)) return chart;
  return { ...chart, display: { ...chart.display, annotations } };
}

/** A default `AnnotationSpec` over `source`, or `null` when that source cannot
 *  carry one. An events source needs a time column to place a mark and two more
 *  columns to label and classify it; guessing at a source with fewer would
 *  produce a spec the validator refuses on save. */
export function defaultAnnotations(source: BiSource): AnnotationSpec | null {
  const columns = source.columns ?? [];
  const at = columns.find((c) => c.role === "time")?.name;
  const dimensions = columns.filter((c) => c.role === "dimension").map((c) => c.name);
  if (!at || dimensions.length === 0) return null;
  return {
    source_id: source.id,
    at_column: at,
    label_column: dimensions[0] as string,
    kind_column: (dimensions[1] ?? dimensions[0]) as string,
    filters: [],
  };
}

export function setChartTitle(chart: ChartSpec, title: string): ChartSpec {
  return { ...chart, slice_name: title };
}

export function setChartDescription(chart: ChartSpec, description: string): ChartSpec {
  return { ...chart, description: description.trim() ? description : null };
}

/**
 * What clicking a field chip does to the selected tile.
 *
 * Role-driven, because the role IS the editing vocabulary: a measure becomes a
 * measure, a dimension becomes the breakdown (or the dimension, when there is
 * none yet), a time column becomes the axis. A raw table is the exception — it
 * has no aggregation at all, so every field simply joins its column list.
 */
export function applyField(chart: ChartSpec, column: BiSourceColumn): ChartSpec {
  if (chart.query.query_mode === "raw") {
    const columns = chart.query.all_columns ?? [];
    if (columns.includes(column.name)) return chart;
    return withQuery(chart, { all_columns: [...columns, column.name] });
  }
  if (column.role === "measure") {
    const measures = getMeasures(chart);
    if (measures.some((m) => m.column === column.name)) return chart;
    const next = [...measures, newMeasure(column)].slice(0, maxMeasures(chart));
    return setMeasures(chart, next);
  }
  if (column.role === "time") return setDimension(chart, column.name);
  if (!getDimension(chart)) return setDimension(chart, column.name);
  return setBreakdown(chart, column.name);
}
